import React, { useState, useEffect } from "react";

import { Modal } from "antd";
import {Button} from "components/Button";

/**
 * @param {boolean} sidebarNavigable
 *   Opt in to leaving the sidebar column clickable while this slide-over is
 *   open. Off by default, so every existing caller keeps today's behaviour.
 *
 *   antd renders a full-viewport `.ant-modal-wrap` at `zIndexPopupBase` (1100),
 *   above the sidebar's 1001 — and it is the *wrap*, not the mask, that eats
 *   the clicks: in antd 5.24 `.ant-modal-mask` is `pointer-events: none` and
 *   rc-dialog hangs the maskClosable handler on the wrap instead. Insetting the
 *   wrap's left edge to the sidebar's on-screen right edge is therefore the
 *   whole fix, and it is scoped to this component.
 *
 *   The alternative — lifting the sidebar above 1100 — was rejected: the z-index
 *   is global to the antd popup family, so it would also put the sidebar over
 *   the confirm dialogs that must keep blocking, and over every Select and
 *   DatePicker panel these forms open.
 *
 *   Anything that opts in must also register an unsaved-changes guard
 *   (`useUnsavedChangesGuard`), or a sidebar click silently discards the form.
 */
const BaseModal = ({ sidebarNavigable = false, ...props }) => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [modalWidth, setModalWidth] = useState(window.innerWidth);
    const [leftMargin, setLeftMargin] = useState(0);
    // The sidebar's on-screen right edge. Deliberately not `offsetWidth`:
    // below 991px the sidebar is parked off-canvas at `margin-left: -225px`
    // (_sidebar.scss:11) while still reporting a 225px width, so offsetWidth
    // would carve out a 225px strip of nothing. getBoundingClientRect().right
    // is 0 exactly when there is no visible sidebar to keep clickable, and
    // tracks the mini-sidebar / hover-expand widths for free.
    const [navInset, setNavInset] = useState(0);
    const HEADER_OFFSET_PX = 64;

    const isOpen = props.open;

  useEffect(() => {
    const sidebar = document.getElementById("sidebar");

    const updateWidths = () => {
      setWindowWidth(window.innerWidth);

      const rect = sidebar ? sidebar.getBoundingClientRect() : null;
      const measuredInset = rect ? Math.max(0, Math.min(rect.right, window.innerWidth)) : 0;
      setNavInset(measuredInset);

      // If screen width is less than 768px (mobile), set modal to 100%
      if (window.innerWidth < 768) {
        setModalWidth(window.innerWidth);
        setLeftMargin(0)
      } else {
        // Reserve space for the sidebar only above 991px, where it is a
        // persistent column (_sidebar.scss:10, `respond-below(custom991)`).
        // At or below 991px the sidebar is an off-canvas drawer — closed, it
        // has nothing on screen to reserve for; slid open, it overlays the
        // content instead of sitting beside it, so it still shouldn't push
        // the panel over. Either way the reservation is 0 and the panel
        // spans the full width, matching pre-`sidebarNavigable` behaviour.
        // measuredInset (not a configured width) is what makes the >991
        // branch correct too: it reflects the sidebar's real on-screen
        // extent, including the mini-sidebar / hover-expand widths.
        const reserved = window.innerWidth > 991 ? measuredInset : 0;
        setModalWidth(window.innerWidth - reserved);
        setLeftMargin(reserved)
      }
    };

    // Run initially, on resize, and whenever the modal is opened — the sidebar
    // can have been collapsed or expanded since the last measurement.
    updateWidths();
    window.addEventListener("resize", updateWidths);

    // The mini-sidebar hover-expand (60px -> 250px) changes the sidebar's width
    // without a window resize, which would otherwise leave the inset stale and
    // the expanded strip unclickable.
    let observer;
    if (sidebar && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateWidths);
      observer.observe(sidebar);
    }

    return () => {
      window.removeEventListener("resize", updateWidths);
      if (observer) observer.disconnect();
    };
  }, [isOpen]);

  // Gated to widths above 991px, the breakpoint at or below which the sidebar
  // stops being a persistent column and becomes an off-canvas drawer
  // (_sidebar.scss:10, `respond-below(custom991)` -> `max-width: 991px`).
  //
  // At or below it, the sidebar slides in on a `margin-left` transition
  // (`.slide-nav .sidebar { margin-left: 0 }`, _sidebar.scss:264). That is a
  // position change, not a size change, so it fires neither `resize` nor the
  // ResizeObserver and `navInset` cannot track it. Rather than add a
  // MutationObserver on body's class to chase it, the feature is simply off
  // below 991px, because it has nothing to offer there: the drawer overlays the
  // content instead of sitting beside it, so at ~390px a 225px sidebar would
  // leave a 165px sliver of form — not a workable "keep the form open while you
  // navigate" affordance. Below 991px behaviour is therefore exactly what it
  // was before this feature existed.
  //
  // This also removes the clipping failure mode: unchecked, a slide-over opened
  // while the mobile nav was already out would compute
  // leftMargin(0) - navInset(225) = -225px and push the panel outside
  // `.ant-modal-wrap`'s `overflow: auto`, permanently cutting 225px off its
  // left edge. Gated, wrapInset is 0 there and `left` stays at leftMargin.
  // Strictly greater than: the SCSS mixin is `respond-below(custom991)`, which
  // compiles to `max-width: 991px`, so 991px itself is already drawer mode. A
  // `>=` here would turn the feature on for the one width where the sidebar is
  // off-canvas, which is the clipping case the paragraph above rules out.
  const navigable = sidebarNavigable && windowWidth > 991;
  const wrapInset = navigable ? navInset : 0;
  // Belt and braces on top of the gate: `left` can never go negative, whatever
  // the sidebar's geometry turns out to be.
  const panelLeft = Math.max(0, leftMargin - wrapInset);

  return (
    <>
      <Modal
        {...props}
        mask={props.mask}
        title={props.title}
        // style={{ top: 100 }}
        open={props.open}
        width={modalWidth}
        height={`calc(100vh - ${HEADER_OFFSET_PX}px)`}
        footer={[]}
        // onOk={() => setModal1Open(false)}
        onCancel={props.onCancel}
        maskClosable={false}
        closable={true}
        closeIcon={<Button aria-hidden="true" icon="fa fa-times" className="btn btn-primary"  />}
        // closeIcon={<span aria-hidden="true">×</span>}
        centered={false}
        style={{
          top: `${HEADER_OFFSET_PX}px`, // Keep modal below the fixed header
          // The panel is absolutely positioned inside `.ant-modal-wrap`, so when
          // the wrap itself is inset the offset has to come back off here.
          // wrapInset is 0 for every caller that has not opted in, and equals
          // leftMargin on a desktop that has, which is why the panel's on-screen
          // position is unchanged in both cases.
          left: panelLeft, // Adjust for sidebar width (example: 200px for a sidebar)
          margin: 0,
          padding: 0,
          position: "absolute", // Ensure it's positioned under the header and sidebar
        //   width: `100vh`, // Fit to the remaining content area width
          height: `calc(100vh - ${HEADER_OFFSET_PX}px)`, // Prevent bottom clipping (esp. mobile)
          maxHeight: `calc(100vh - ${HEADER_OFFSET_PX}px)`,
        }}
        styles={{
          body: {
            // Keep bottom CTA visible above mobile browser bars / iOS safe-area.
            paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
            overflowY: "auto",
          },
          // `.ant-modal-wrap` is `position: fixed; inset: 0` at zIndexPopupBase.
          // Pulling its left edge in past the sidebar is what makes the sidebar
          // clickable, while the wrap still covers — and so still blocks — the
          // whole content area to the right of it.
          ...(wrapInset ? { wrapper: { left: wrapInset } } : null),
        }}
        // bodyStyle={{
        //     display: "flex", // Flexbox for content stretching
        //     flexDirection: "column", // Vertical stacking
        //     padding: 0, // Remove default padding
        //     height: "100%", // Fill modal height
        //     overflow: "hidden", // Prevent modal overflow
        //   }}
      >
        {props.children}
      </Modal>
    </>
  );
};

export default BaseModal;
